'use client'

import Pill from '@/components/ui/Pill'
import Link from '@/components/ui/Link'
import { PREFETCH } from '@/lib/prefetch'
import AppIcon from '@/components/content/app/AppIcon'
import { getBrandColors, cn, getOverlappingAppCategory } from '@/lib/utils'
import { createLogger } from '@/lib/logger'
import type {
  Comparison,
  ComparisonAppsApp,
  App,
  AppCategoryRecommendation,
  Category,
  Media,
} from '@/payload-types'

const logger = createLogger('ComparisonsRow')

type ComparisonWithApps = Pick<Comparison, 'id' | 'name' | 'comparisonApps'>

function getPreferredCategory(apps: App[], preferredCategoryId: Category['id']) {
  const matchingApps = apps.filter((app) => {
    const categoryRecommendations =
      (app.categoryRecommendations?.docs as AppCategoryRecommendation[] | undefined) ?? []

    return categoryRecommendations.some((recommendation) => {
      const category = recommendation.category
      const categoryId = typeof category === 'number' ? category : category?.id
      return categoryId === preferredCategoryId
    })
  })

  if (matchingApps.length < 2) return undefined

  for (const app of matchingApps) {
    const categoryRecommendations =
      (app.categoryRecommendations?.docs as AppCategoryRecommendation[] | undefined) ?? []

    const matchingRecommendation = categoryRecommendations.find((recommendation) => {
      const category = recommendation.category
      const categoryId = typeof category === 'number' ? category : category?.id
      return categoryId === preferredCategoryId && typeof category !== 'number'
    })

    if (matchingRecommendation && typeof matchingRecommendation.category !== 'number') {
      return matchingRecommendation.category
    }
  }

  return undefined
}

export default function ComparisonRowThumbnail({
  comparison,
  inline,
  invert,
  preferredCategoryId,
}: {
  comparison: ComparisonWithApps
  inline?: boolean
  invert?: boolean
  preferredCategoryId?: Category['id']
}) {
  const comparisonApps = (comparison.comparisonApps?.docs as ComparisonAppsApp[])?.filter(
    (ca) => ca.app && typeof ca.app !== 'number' && (ca.app as App).slug,
  )
  const apps: App[] = comparisonApps?.map((comparisonApp) => comparisonApp.app as App) ?? []

  const preferredCategory = preferredCategoryId
    ? getPreferredCategory(apps, preferredCategoryId)
    : undefined

  const overlappingCategory = preferredCategory ?? getOverlappingAppCategory(apps)

  const link = `/compare/${(comparisonApps?.sort((a, b) => a.order - b.order) ?? [])
    .map((obj) => (obj.app as App).slug)
    .join('-vs-')}`

  return (
    <div key={comparison.id} className="flex w-full flex-col gap-3">
      <Link
        href={link}
        prefetch={PREFETCH}
        className={cn(
          'relative flex h-[200px] w-full shrink-0 overflow-hidden rounded-icon-sm border border-black/10 shadow-inner',
          inline && 'h-[300px]',
        )}
      >
        {apps.map((app, index) => {
          const appIcon = app.icon as Media
          const appGlyph = app.glyph as Media
          if (typeof appIcon === 'number' || typeof appGlyph === 'number') {
            logger.error(
              `appIcon is not a Media object in ComparisonRow (ComparisonThumbnail ComparisonRowThumbnail app icon`,
            )
            return null
          }
          const { backgroundColor } = getBrandColors(app)
          return (
            <div
              key={`${app.id}-${index}`}
              className="absolute top-0 h-full w-full"
              style={{
                backgroundColor,
                clipPath:
                  index === 0
                    ? 'polygon(0 0, 60% 0, 40% 100%, 0% 100%)'
                    : 'polygon(60% 0, 100% 0, 100% 100%, 40% 100%)',
              }}
            >
              <div
                className={cn(
                  'absolute top-1/2 flex -translate-y-1/2 items-center justify-center',
                  index === 0 ? 'left-1/4 -translate-x-1/2' : 'right-1/4 translate-x-1/2',
                  inline && 'scale-150',
                )}
              >
                <AppIcon
                  icon={appIcon}
                  glyph={appGlyph}
                  name={app.name}
                  background={backgroundColor}
                  invert={backgroundColor === '#000000'}
                  size={64}
                />
              </div>
            </div>
          )
        })}
      </Link>
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-col">
          <Link
            href={link}
            prefetch={PREFETCH}
            className={cn('line-clamp-1 font-semibold', invert ? 'text-white' : 'text-gray-950')}
          >
            {comparison.name}
          </Link>

          {overlappingCategory && (
            <span
              className={cn(
                'line-clamp-1 w-full text-13',
                invert ? 'text-white/60' : 'text-gray-600',
              )}
            >
              {overlappingCategory.name}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Link href={link} prefetch={PREFETCH}>
            <Pill style="outlinePrimary" theme={invert ? 'dark' : 'light'} className="ml-auto">
              Read
            </Pill>
          </Link>
        </div>
      </div>
    </div>
  )
}
